import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { VideoUploadService, InitializeUploadRequest } from '@solverml/api';
import { lastValueFrom } from 'rxjs';

export interface VideoMetadata {
  title: string;
  description: string;
  gender?: string;
  category?: string;
  competition?: string;
  duration_in_seconds?: number;
  season_id?: number;
  match_date?: string;
}

export interface VideoUploadProgress {
  progress: number;
  status: 'pending' | 'uploading' | 'processing' | 'complete' | 'error';
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class VideosUploadService {
  private static readonly CHUNK_SIZE_BYTES = 50 * 1024 * 1024; // 50MB
  private static readonly MAX_CHUNK_RETRIES = 3;
  private static readonly RETRY_BASE_DELAY_MS = 800;
  private static readonly RECOVERABLE_400_MARKERS = [
    'parsing the body',
    'multipart',
    'stream',
    'connection reset',
    'timed out',
    'timeout',
    'temporarily unavailable',
    'chunk size mismatch',
    'chunk integrity mismatch',
    'empty chunk payload',
    'desajuste de tamaño del chunk',
    'integridad del chunk inválida',
    'el chunk está vacío'
  ];

  constructor(
    private videoUploadApiService: VideoUploadService
  ) { }

  /**
   * Upload a video file.
   * @param file The video file
   * @param metadata Video metadata
   * @param matchRecord Optional match record file
   * @param progressCallback Callback function to track progress
   * @returns The video ID
   */
  async uploadVideo(
    file: File | null,
    metadata: VideoMetadata,
    matchRecord: File | null,
    progressCallback: (progress: VideoUploadProgress) => void
  ): Promise<string> {
    try {
      if (!file) {
        throw new Error("No se ha proporcionado ningún archivo para la subida.");
      }

      progressCallback({
        progress: 0,
        status: 'pending',
        message: 'Preparando para subir el video...'
      });

      // 1. Prepare InitializeUploadRequest DTO
      const initRequestData: any = {
        title: metadata.title,
        description: metadata.description,
        gender: metadata.gender,
        category: metadata.category,
        competition: metadata.competition,
        duration_in_seconds: metadata.duration_in_seconds,
        season_id: metadata.season_id ?? 1,
        match_date: metadata.match_date ?? new Date().toISOString().slice(0, 10)
      };

      // Call Initialize upload
      const initResponse = await lastValueFrom(
        this.videoUploadApiService.initializeVideoUpload(initRequestData as InitializeUploadRequest)
      );

      const videoId = initResponse.videoId;

      // Upload PDF first if provided
      if (matchRecord) {
        await this.uploadMatchRecord(videoId, matchRecord, progressCallback);
      }

      // 2. Upload Chunks
      const chunkSize = VideosUploadService.CHUNK_SIZE_BYTES;
      const totalChunks = Math.ceil(file.size / chunkSize);

      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(file.size, start + chunkSize);
        const chunk = file.slice(start, end);
        const chunkSha256 = await this.computeChunkSha256Hex(chunk);

        await this.uploadChunkWithRetry(
          chunk,
          videoId,
          i,
          totalChunks,
          progressCallback,
          chunk.size,
          chunkSha256
        );

        const progress = Math.round(((i + 1) / totalChunks) * 100);
        progressCallback({
          progress: progress,
          status: 'uploading',
          message: `Subiendo parte ${i + 1} de ${totalChunks}...`
        });
      }

      // 3. Finalize
      progressCallback({ progress: 100, status: 'processing', message: 'Subida completa. Finalizando...' });
      const endResponse = await lastValueFrom(
        this.videoUploadApiService.finalizeVideoUpload(videoId)
      );
      console.log('Upload finalized:', endResponse);

      progressCallback({
        progress: 100,
        status: 'processing',
        message: 'Subida completa. Procesando el video...'
      });

      return videoId;

    } catch (error) {
      console.error("Upload Service Error:", error);
      const errorMessage = this.extractErrorMessage(error);

      progressCallback({
        progress: 0,
        status: 'error',
        message: `Error: ${errorMessage}`
      });
      throw error;
    }
  }

  private async uploadChunkWithRetry(
    chunk: Blob,
    videoId: string,
    chunkIndex: number,
    totalChunks: number,
    progressCallback: (progress: VideoUploadProgress) => void,
    chunkSize: number,
    chunkSha256: string | null
  ): Promise<void> {
    let retries = 0;

    while (true) {
      try {
        await this.uploadChunkWithIntegrity(chunk, videoId, chunkIndex, chunkSize, chunkSha256);
        return;
      } catch (error) {
        const canRetry = retries < VideosUploadService.MAX_CHUNK_RETRIES && this.isRecoverableChunkError(error);
        if (!canRetry) {
          throw error;
        }

        retries += 1;
        const progressBeforeChunk = Math.round((chunkIndex / totalChunks) * 100);
        progressCallback({
          progress: progressBeforeChunk,
          status: 'uploading',
          message: `Reintentando parte ${chunkIndex + 1} de ${totalChunks} (intento ${retries}/${VideosUploadService.MAX_CHUNK_RETRIES})...`
        });

        await this.delay(this.getRetryDelayMs(retries));
      }
    }
  }

  private isRecoverableChunkError(error: unknown): boolean {
    const httpError = error as HttpErrorResponse;
    const status = typeof httpError?.status === 'number' ? httpError.status : undefined;

    if (status === 0 || status === 408 || status === 429) {
      return true;
    }

    if (status !== undefined && status >= 500) {
      return true;
    }

    if (status === 400) {
      const detail = this.extractErrorMessage(error).toLowerCase();
      return VideosUploadService.RECOVERABLE_400_MARKERS.some(marker => detail.includes(marker));
    }

    return false;
  }

  private getRetryDelayMs(retryAttempt: number): number {
    const delayMs = VideosUploadService.RETRY_BASE_DELAY_MS * Math.pow(2, retryAttempt - 1);
    return Math.min(delayMs, 6000);
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async uploadChunkWithIntegrity(
    chunk: Blob,
    videoId: string,
    chunkIndex: number,
    chunkSize: number,
    chunkSha256: string | null
  ): Promise<void> {
    await lastValueFrom(
      this.videoUploadApiService.uploadChunk(
        chunk,
        videoId,
        chunkIndex,
        chunkSize,
        chunkSha256 ?? undefined,
        'body',
        false
      )
    );
  }

  private async computeChunkSha256Hex(chunk: Blob): Promise<string | null> {
    if (!globalThis.crypto?.subtle) {
      return null;
    }

    const buffer = await chunk.arrayBuffer();
    const digest = await globalThis.crypto.subtle.digest('SHA-256', buffer);
    const hashBytes = Array.from(new Uint8Array(digest));
    return hashBytes.map((value) => value.toString(16).padStart(2, '0')).join('');
  }

  private extractErrorMessage(error: any): string {
    let errorMessage = 'Error desconocido durante la subida.';
    if (error instanceof Error) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    if (error && error.body && error.body.detail) {
      errorMessage = error.body.detail;
    } else if (error && error.error && error.error.detail) {
      errorMessage = error.error.detail;
    }

    return errorMessage;
  }

  // --- Helper for Match Record Upload ---
  private async uploadMatchRecord(videoId: string, matchRecord: File, progressCallback: (progress: VideoUploadProgress) => void): Promise<void> {
    try {
      progressCallback({ progress: 0, status: 'pending', message: 'Subiendo acta del partido...' });
      await lastValueFrom(this.videoUploadApiService.uploadMatchRecord(videoId, matchRecord));
      progressCallback({ progress: 0, status: 'pending', message: 'Acta subida. Preparando vídeo...' });
    } catch (pdfError) {
      console.error("PDF Upload Error:", pdfError);
      throw new Error(`Error al subir el acta PDF: ${pdfError.message || 'Error desconocido'}`);
    }
  }
}
